<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="preview-sheet"
      :class="{
        'from-side': menusShort && !wide,
        'wide-row': menusShort && wide,
        'has-footer': hasFooter,
        armed,
      }"
      @pointerup.self="onDismiss"
    >
      <div
        class="preview-sheet-panel"
        @pointerdown.stop
        @pointerup.stop
        @click.stop
      >
        <CloseXButton
          class="preview-sheet-close"
          :label="t('mobile.close')"
          @click="$emit('close')"
        />
        <div class="preview-sheet-body">
          <slot />
        </div>
        <div v-if="hasFooter" class="preview-sheet-footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, useSlots, watch } from 'vue'
import { useI18n } from '../i18n/t'
import { useCompactLayout } from '../composables/useCompactLayout'
import CloseXButton from './CloseXButton.vue'

const props = defineProps<{
  visible: boolean
  /** Landscape: wide dialog with room for art left and text right. */
  wide?: boolean
}>()
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const { menusShort } = useCompactLayout()
const slots = useSlots()
const hasFooter = computed(() => !!slots.footer)
const armed = ref(false)
let armTimer: ReturnType<typeof setTimeout> | null = null

watch(
  () => props.visible,
  async (on) => {
    armed.value = false
    if (armTimer) {
      clearTimeout(armTimer)
      armTimer = null
    }
    if (!on) return
    await nextTick()
    armTimer = setTimeout(() => {
      armed.value = true
      armTimer = null
    }, 400)
  },
)

function onDismiss(): void {
  if (!armed.value) return
  emit('close')
}

onUnmounted(() => {
  if (armTimer) clearTimeout(armTimer)
})
</script>

<style scoped>
.preview-sheet {
  position: fixed;
  inset: 0;
  z-index: 240;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 max(8px, env(safe-area-inset-right))
    max(8px, env(safe-area-inset-bottom))
    max(8px, env(safe-area-inset-left));
  background: rgba(15, 23, 42, 0.55);
}

.preview-sheet:not(.armed) {
  pointer-events: none;
}

.preview-sheet:not(.armed) .preview-sheet-panel {
  pointer-events: auto;
}

.preview-sheet.from-side {
  align-items: stretch;
  justify-content: flex-end;
  padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px)
    env(safe-area-inset-bottom, 0px) 0;
}

.preview-sheet-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(320px, 100%);
  max-height: min(88vh, 720px);
  overflow: hidden;
  padding: 36px 12px 12px;
  border-radius: 16px 16px 8px 8px;
  background: #0f172a;
  border: 1px solid #334155;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.45);
}

.has-footer .preview-sheet-panel {
  padding-bottom: 0;
}

.from-side .preview-sheet-panel {
  width: min(320px, 46vw);
  max-height: none;
  height: 100%;
  border-radius: 12px 0 0 12px;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.45);
}

.preview-sheet.wide-row {
  align-items: center;
  justify-content: center;
  padding: max(8px, env(safe-area-inset-top, 0px))
    max(12px, env(safe-area-inset-right, 0px))
    max(8px, env(safe-area-inset-bottom, 0px))
    max(12px, env(safe-area-inset-left, 0px));
}

.wide-row .preview-sheet-panel {
  width: min(820px, 96vw);
  height: min(380px, calc(100dvh - 16px));
  max-height: calc(100dvh - 16px);
  border-radius: 12px;
}

.wide-row .preview-sheet-body {
  overflow: hidden;
  display: flex;
}

.preview-sheet-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}

.preview-sheet-footer {
  flex-shrink: 0;
  padding: 12px 0 max(16px, env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #334155;
  background: #0f172a;
}

.preview-sheet-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  background: #1e293b;
  color: #e2e8f0;
}
</style>
